import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { Send, Bot, User , Clock, CheckCircle } from "lucide-react";
import Tooltip from "../components/JobSeekerTooltip";
import { useProctoring } from "../../hooks/useProctoring";
import ProctoringBanner from "../../components/Proctoring/ProctoringBanner";
import ProctoringSetupModal from "../../components/Proctoring/ProctoringSetupModal";
import FloatingCameraWidget from "../../components/Proctoring/FloatingCameraWidget";
import VerificationGuard from "../../components/VerificationGuard";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import { checkTestAchievements } from "../../services/gamificationService";

const AIInterview = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);

  // Check if this interview is from an HR-shared test (bypasses verification)
  const isHRSharedTest = searchParams.get("source") === "hr-shared";
  const { isVerified } = useVerificationStatus();

  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [showProctoringSetup, setShowProctoringSetup] = useState(false);
  const [proctoringInitialized, setProctoringInitialized] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize proctoring
  const {
    proctoringStatus,
    startProctoring,
    stopProctoring,
    clearViolations,
    addViolation
  } = useProctoring({
    enableTabSwitchDetection: true,
    enableCopyPasteDetection: true,
    enableFullscreenEnforcement: true,
    enableRightClickDisable: true,
    enableKeyboardShortcuts: true,
    enableCameraMonitoring: true,
    enableMicrophoneMonitoring: true,
    maxViolations: 5,
    onViolation: (violation) => {
      // Log violation to Firebase
      if (sessionId && user) {
        logViolationToFirebase(violation);
      }
    },
    onMaxViolationsReached: () => {
      // Terminate the interview
      alert('Too many proctoring violations detected. The interview will be terminated.');
      handleTerminateInterview();
    }
  });

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load interview session
  useEffect(() => {
    if (!sessionId || !user) return;

    const sessionDoc = doc(db, "interviewSessions", sessionId);
    const unsubscribe = onSnapshot(sessionDoc, (snapshot) => {
      if (snapshot.exists()) {
        const sessionData = snapshot.data() ;
        setSession(sessionData);

        // Convert conversation history to messages
        const messageHistory = [];
        sessionData.conversationHistory?.forEach((conv, index) => {
          messageHistory.push({
            id: `q-${index}`,
            sender: "ai",
            message: conv.question,
            timestamp: conv.timestamp,
            questionNumber: conv.questionNumber,
          });
          if (conv.answer) {
            messageHistory.push({
              id: `a-${index}`,
              sender: "user",
              message: conv.answer,
              timestamp: conv.timestamp,
            });
          }
        });
        setMessages(messageHistory);

        // If session is completed, show final results
        if (sessionData.status === "completed") {
          // Stop proctoring before redirecting
          stopProctoring();
          // Pass verification prompt for HR-shared tests with unverified users
          if (isHRSharedTest && !isVerified) {
            navigate(`/job-seeker/interview-results/${sessionId}?showVerificationPrompt=true`);
          } else {
            navigate(`/job-seeker/interview-results/${sessionId}`);
          }
        } else if (!proctoringInitialized && sessionData.status === "in_progress") {
          // Show proctoring setup for active sessions
          setShowProctoringSetup(true);
        }
      } else {
        setError("Interview session not found");
      }
    });

    return () => unsubscribe();
  }, [sessionId, user, db, navigate, proctoringInitialized, stopProctoring]);

  // Generate next question using OpenAI GPT-5.2
  const generateNextQuestion = async (
    conversationHistory,
    questionNumber,
    jobDescription
  ) => {
    const prompt = `You are a STRICT, PROFESSIONAL, and HIGHLY EXPERIENCED technical interviewer. You are conducting an interview for this exact position:

===== JOB DESCRIPTION =====
${jobDescription}

===== INTERVIEW CONTEXT =====
- This is question ${questionNumber} of 10 total questions
- You are evaluating for technical competency, problem-solving ability, communication skills, and job fit
- This interview determines 75% of the candidate's final score
- Be STRICT in your assessment - this is a competitive position

===== PREVIOUS CONVERSATION =====
${conversationHistory
  .map(
    (conv, i) =>
      `QUESTION ${i + 1}: ${conv.question}\nCANDIDATE ANSWER ${i + 1}: ${
        conv.answer || "No answer provided"
      }`
  )
  .join("\n\n")}

===== YOUR TASK =====
Generate Question ${questionNumber} that:
1. Is DIRECTLY related to specific requirements in the job description
2. Builds upon the candidate's previous responses (analyze their competency level)
3. Tests technical knowledge, problem-solving, or job-specific skills
4. Is appropriate for someone applying for this exact role
5. Requires a detailed, thoughtful response
6. Helps assess if they truly fit this position

===== REQUIREMENTS =====
- Generate ONLY the interview question, no additional text
- Make it specific to the job requirements above
- Ensure it requires substantial knowledge to answer well
- Keep it professional and direct

QUESTION ${questionNumber}:`;

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateInterviewQuestion",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            skills: session.jobDescription.split('\n').slice(0, 5),
            previousAnswers: conversationHistory.map(conv => ({
              question: conv.question,
              answer: conv.answer
            }))
          }),
        }
      );

      const data = await response.json();
      return (
        data.question?.trim() ||
        "Could you tell me about your experience with the key technologies mentioned in this job description?"
      );
    } catch (error) {
      console.error("Error generating question:", error);
      return "Could you tell me about your experience with the key technologies mentioned in this job description?";
    }
  };

  // Send user answer and get next question
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !session || isLoading) return;

    setIsLoading(true);
    setIsTyping(true);

    try {
      const userAnswer = currentMessage.trim();
      const currentQ = session.currentQuestionNumber;

      // Update conversation history
      const updatedHistory = [...(session.conversationHistory || [])];
      if (updatedHistory[currentQ - 1]) {
        updatedHistory[currentQ - 1].answer = userAnswer;
        updatedHistory[currentQ - 1].timestamp = new Date();
      }

      // Update session with user's answer
      await updateDoc(doc(db, "interviewSessions", sessionId), {
        conversationHistory: updatedHistory,
      });

      setCurrentMessage("");

      // Check if we've completed all 10 questions
      if (currentQ >= 10) {
        // Interview complete - generate final assessment
        await completeInterview(updatedHistory);
        return;
      }

      // Generate next question
      const nextQuestionNumber = currentQ + 1;
      const nextQuestion = await generateNextQuestion(
        updatedHistory,
        nextQuestionNumber,
        session.jobDescription
      );

      // Add next question to conversation
      const newConversation = {
        questionNumber: nextQuestionNumber,
        question: nextQuestion,
        answer: "",
        timestamp: new Date(),
      };

      updatedHistory.push(newConversation);

      // Update session with next question
      await updateDoc(doc(db, "interviewSessions", sessionId), {
        conversationHistory: updatedHistory,
        currentQuestionNumber: nextQuestionNumber,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  // Complete interview and generate final assessment
  const completeInterview = async (conversationHistory) => {
    try {
      setIsTyping(true);

      // Validate conversation history
      if (!conversationHistory || conversationHistory.length === 0) {
        throw new Error("No conversation history found for assessment");
      }

      console.log("Starting AI assessment with conversation history:", conversationHistory);

      // Generate final assessment
      const assessmentPrompt = `You are a STRICT TECHNICAL INTERVIEWER evaluating a candidate for this specific position. You must be extremely thorough and critical in your assessment.

===== JOB DESCRIPTION =====
${session?.jobDescription}

===== COMPLETE INTERVIEW TRANSCRIPT =====
${conversationHistory
  .map(
    (conv, i) =>
      `QUESTION ${i + 1}: ${conv.question || "No question"}

CANDIDATE RESPONSE ${i + 1}: ${conv.answer || "No answer provided"}

---`
  )
  .join("\n\n")}

===== ASSESSMENT CRITERIA =====
You must evaluate each area strictly based on the job requirements:

1. TECHNICAL KNOWLEDGE (25 points max):
   - Deep understanding of required technologies/skills
   - Accurate technical explanations
   - Awareness of best practices and industry standards

2. PROBLEM-SOLVING ABILITY (25 points max):
   - Logical thinking and approach
   - Ability to break down complex problems
   - Creative and practical solutions

3. COMMUNICATION SKILLS (15 points max):
   - Clear, articulate explanations
   - Professional communication style
   - Ability to explain technical concepts

4. JOB FIT & EXPERIENCE (10 points max):
   - Relevant experience for this specific role
   - Understanding of job responsibilities
   - Cultural and technical fit

===== SCORING GUIDELINES =====
- Total score out of 75 points
- Be EXTREMELY STRICT - this is a competitive position
- Deduct points for: vague answers, incorrect information, lack of depth
- Award full points ONLY for exceptional, detailed, accurate responses
- Consider the job description requirements  benchmark
- If a candidate provides no answer or very poor answers, give low scores accordingly
- Ensure finalScore equals the sum of all detailed scores

===== REQUIRED JSON FORMAT =====
Return ONLY this JSON structure with valid numeric scores:
{
  "finalScore": [number between 0-75],
  "detailedScoring": {
    "technicalKnowledge": [score out of 25],
    "problemSolving": [score out of 25],
    "communication": [score out of 15],
    "jobFit": [score out of 10]
  },
  "structuredAssessment": {
    "technical": "[score]/10",
    "communication": "[score]/10", 
    "problemSolving": "[score]/10",
    "jobFit": "[score]/10"
  },
  "narrativeAssessment": "[2-3 paragraph detailed assessment explaining strengths, weaknesses, and overall fit for this specific position]",
  "strengths": ["specific strength 1", "specific strength 2", "specific strength 3"],
  "weaknesses": ["specific weakness 1", "specific weakness 2", "specific weakness 3"],
  "recommendationForHiring": "[STRONG RECOMMEND / RECOMMEND / NEUTRAL / NOT RECOMMEND / STRONG REJECT]",
  "keyFindings": "[One sentence summary of the candidate's suitability for this role]"
}`;

      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      const idToken = await currentUser.getIdToken();

      const response = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/assessInterview",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            skills: session.jobDescription.split('\n').slice(0, 5),
            qaHistory: conversationHistory.map(conv => ({
              question: conv.question,
              answer: conv.answer
            }))
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const assessment = await response.json();
      console.log("Assessment response:", assessment);

      // Validate and sanitize assessment data
      if (!assessment || typeof assessment.finalScore !== 'number') {
        console.error("Invalid assessment structure:", assessment);
        throw new Error("Assessment structure is invalid or missing finalScore");
      }

      // Ensure scores are numeric and within valid ranges
      const sanitizedAssessment = {
        finalScore: Math.min(Math.max(Number(assessment.finalScore) || 0, 0), 75),
        detailedScoring: {
          technicalKnowledge: Math.min(Math.max(Number(assessment.detailedScoring?.technicalKnowledge) || 0, 0), 25),
          problemSolving: Math.min(Math.max(Number(assessment.detailedScoring?.problemSolving) || 0, 0), 25),
          communication: Math.min(Math.max(Number(assessment.detailedScoring?.communication) || 0, 0), 15),
          jobFit: Math.min(Math.max(Number(assessment.detailedScoring?.jobFit) || 0, 0), 10)
        },
        structuredAssessment: assessment.structuredAssessment || {
          technical: "0/10",
          communication: "0/10",
          problemSolving: "0/10",
          jobFit: "0/10"
        },
        narrativeAssessment: assessment.narrativeAssessment || "Assessment could not be generated.",
        strengths: Array.isArray(assessment.strengths) ? assessment.strengths : ["Unable to identify strengths"],
        weaknesses: Array.isArray(assessment.weaknesses) ? assessment.weaknesses : ["Unable to identify weaknesses"],
        recommendationForHiring: assessment.recommendationForHiring || "NEUTRAL",
        keyFindings: assessment.keyFindings || "Assessment incomplete"
      };

      console.log("Sanitized assessment:", sanitizedAssessment);

      // Update session  with comprehensive data
      await updateDoc(doc(db, "interviewSessions", sessionId), {
        status: "completed",
        finalScore: sanitizedAssessment.finalScore,
        finalAssessment: JSON.stringify(sanitizedAssessment),
        completedAt: serverTimestamp(),
        // Add detailed scoring breakdown
        detailedScoring: sanitizedAssessment.detailedScoring,
        recommendationForHiring: sanitizedAssessment.recommendationForHiring,
        keyFindings: sanitizedAssessment.keyFindings,
        // Store conversation count for verification
        totalQuestionsAsked: conversationHistory.length,
        assessmentStatus: "completed", // Add new status field
      });

      console.log("Interview session updated with assessment");

      // Update jobseeker results with comprehensive interview data
      const jobseekerResultDoc = doc(
        db,
        "jobseekerResults",
        `${user?.id}_${session?.testId}`
      );
      const jobseekerResult = await getDoc(jobseekerResultDoc);

      if (jobseekerResult.exists()) {
        const existingData = jobseekerResult.data();
        const mcqScore = Math.min((existingData.score / 100) * 25, 25); // Cap at 25
        const interviewScore = sanitizedAssessment.finalScore;
        const totalScore = mcqScore + interviewScore;

        console.log("Updating jobseeker results with scores:", {
          mcqScore,
          interviewScore,
          totalScore,
          sanitizedAssessment
        });

        const updateData = {
          // Core scores
          mcqScore,
          interviewScore,
          totalScore,

          // Interview metadata
          interviewSessionId: sessionId,
          hasCompletedInterview: true,
          interviewCompletedAt: serverTimestamp(),
          assessmentStatus: "completed", // Add new status field

          // Assessment details for HR view
          jobFitAssessment: JSON.stringify(sanitizedAssessment),
          recommendationForHiring: sanitizedAssessment.recommendationForHiring,
          keyFindings: sanitizedAssessment.keyFindings,

          // Detailed scoring breakdown
          detailedScoring: sanitizedAssessment.detailedScoring,

          // For badge calculation
          performanceLevel:
            totalScore >= 90
              ? "excellent"
              : totalScore >= 75
              ? "good"
              : totalScore >= 60
              ? "average"
              : "below_average",

          // Update completion timestamp
          completedAt: serverTimestamp(),

          // Store original data
          originalMcqScore: existingData.score,
          testTakenAt: existingData.completedAt,

          // Add qualified status
          qualifiedForInterview: true, // They reached this point, so they qualified
        };

        console.log("Updating document with data:", updateData);
        await updateDoc(jobseekerResultDoc, updateData);
        console.log("Jobseeker results updated successfully");

        // Check for achievements after interview completion
        try {
          console.log("🏆 Checking for unlocked achievements...");
          await checkTestAchievements(user?.id, totalScore);
          console.log("✅ Achievement check completed!");
        } catch (achievementError) {
          console.error("⚠️ Achievement check failed (non-critical):", achievementError);
        }
      } else {
        console.error("Jobseeker result document not found:", `${user?.id}_${session?.testId}`);
        throw new Error("Could not find jobseeker result document to update");
      }
    } catch (error) {
      console.error("Error completing interview:", error);
      setError("Failed to complete interview assessment");

      try {
        // If assessment fails, update status accordingly but provide a fallback score
        await updateDoc(doc(db, "interviewSessions", sessionId), {
          status: "completed",
          assessmentStatus: "failed",
          error: (error ).message,
          finalScore: 0, // Fallback score
        });

        const jobseekerResultDoc = doc(
          db,
          "jobseekerResults",
          `${user?.id}_${session?.testId}`
        );
        
        const jobseekerResult = await getDoc(jobseekerResultDoc);
        if (jobseekerResult.exists()) {
          const existingData = jobseekerResult.data();
          const mcqScore = Math.min((existingData.score / 100) * 25, 25);
          const interviewScore = 0; // Fallback score on error
          const totalScore = mcqScore + interviewScore;

          await updateDoc(jobseekerResultDoc, {
            hasCompletedInterview: true,
            interviewCompletedAt: serverTimestamp(),
            assessmentStatus: "failed",
            interviewScore, // Set to 0 instead of null
            totalScore, // Still calculate total with MCQ score
            mcqScore,
            error: `AI assessment failed: ${error.message}`,
            // Provide minimal fallback assessment data
            jobFitAssessment: JSON.stringify({
              finalScore: 0,
              narrativeAssessment: "Assessment could not be completed due to technical issues.",
              recommendationForHiring: "NEUTRAL",
              keyFindings: "Assessment incomplete due to system error"
            }),
            recommendationForHiring: "NEUTRAL",
            keyFindings: "Assessment incomplete due to system error",
            completedAt: serverTimestamp(),
          });
        }
      } catch (updateError) {
        console.error("Failed to update documents after assessment error:", updateError);
      }
    } finally {
      setIsTyping(false);
      // Navigate regardless of success or failure, the results page will handle the state
      // Pass verification prompt for HR-shared tests with unverified users
      if (isHRSharedTest && !isVerified) {
        navigate(`/job-seeker/interview-results/${sessionId}?showVerificationPrompt=true`);
      } else {
        navigate(`/job-seeker/interview-results/${sessionId}`);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Log violations to Firebase
  const logViolationToFirebase = async (violation) => {
    try {
      const sessionDoc = doc(db, "interviewSessions", sessionId);
      const sessionSnapshot = await getDoc(sessionDoc);

      if (sessionSnapshot.exists()) {
        const sessionData = sessionSnapshot.data();
        const existingViolations = sessionData.proctoringViolations || [];

        await updateDoc(sessionDoc, {
          proctoringViolations: [
            ...existingViolations,
            {
              type: violation.type,
              timestamp: violation.timestamp,
              details: violation.details,
              userId: user?.id
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to log violation:', error);
    }
  };

  // Handle interview termination due to violations
  const handleTerminateInterview = async () => {
    try {
      await updateDoc(doc(db, "interviewSessions", sessionId), {
        status: "terminated",
        terminationReason: "Too many proctoring violations",
        terminatedAt: serverTimestamp()
      });

      // Update jobseeker results
      const jobseekerResultDoc = doc(db, "jobseekerResults", `${user?.id}_${session?.testId}`);
      await updateDoc(jobseekerResultDoc, {
        interviewStatus: "terminated",
        interviewScore: 0,
        terminationReason: "Proctoring violations",
        hasCompletedInterview: false
      });

      stopProctoring();
      navigate('/job-seeker');
    } catch (error) {
      console.error('Failed to terminate interview:', error);
    }
  };

  // Handle proctoring setup completion
  const handleStartProctoring = async () => {
    try {
      await startProctoring();
      setProctoringInitialized(true);
      setShowProctoringSetup(false);

      // Log proctoring start
      await updateDoc(doc(db, "interviewSessions", sessionId), {
        proctoringStarted: true,
        proctoringStartedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to start proctoring:', error);
      alert('Failed to initialize proctoring. Please refresh and try again.');
    }
  };

  // Handle stopping proctoring
  const handleStopProctoring = () => {
    stopProctoring();
    setProctoringInitialized(false);
  };

  // Cleanup proctoring on unmount
  useEffect(() => {
    return () => {
      stopProctoring();
    };
  }, [stopProctoring]);

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading interview session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">Error</div>
          <Tooltip content="Return to your dashboard">
            <button
              onClick={() => navigate("/job-seeker")}
              className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 relative"
            >
              Return to Dashboard
            </button>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <VerificationGuard requireVerification={!isHRSharedTest}>
      <div className="min-h-screen bg-gray-50">
        {/* Proctoring Banner */}
        <ProctoringBanner
          proctoringStatus={proctoringStatus}
          onStopProctoring={handleStopProctoring}
          showViolationDetails={true}
        />

      {/* Proctoring Setup Modal */}
      <ProctoringSetupModal
        isOpen={showProctoringSetup}
        onClose={() => {
          setShowProctoringSetup(false);
          navigate('/job-seeker');
        }}
        onStartProctoring={handleStartProctoring}
        requireCamera={true}
        requireMicrophone={true}
      />

      {/* Header */}
      <div className={`bg-white border-b border-gray-200 px-6 py-4 ${proctoringStatus.isActive ? 'mt-16' : ''}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                AI Technical Interview
              </h1>
              <p className="text-sm text-gray-600">
                Round 2 of 2 • Question {session.currentQuestionNumber} of 10
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-600">No time limit</span>
              </div>
              {proctoringStatus.isActive && (
                <div className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                  PROCTORED
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Interview Notice */}
      <div className={`${proctoringStatus.isActive ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'} border-b px-6 py-3`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3">
            <Bot className={`w-5 h-5 ${proctoringStatus.isActive ? 'text-red-600' : 'text-blue-600'}`} />
            <p className={`text-sm ${proctoringStatus.isActive ? 'text-red-800' : 'text-blue-800'}`}>
              {proctoringStatus.isActive
                ? 'This interview is being proctored. Tab switching, copy/paste, and other violations are monitored and may result in test termination.'
                : 'You are now in an AI-powered technical interview. Answer each question thoughtfully and thoroughly. This round is worth 75% of your total score.'}
            </p>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-[600px] flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`flex items-start space-x-3 max-w-3xl ${
                    message.sender === "user"
                      ? "flex-row-reverse space-x-reverse"
                      : ""
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      message.sender === "ai" ? "bg-blue-100" : "bg-red-100"
                    }`}
                  >
                    {message.sender === "ai" ? (
                      <Bot className="w-4 h-4 text-blue-600" />
                    ) : (
                      <User className="w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <div
                    className={`rounded-lg px-4 py-3 ${
                      message.sender === "user"
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    {message.questionNumber && (
                      <div className="text-xs opacity-75 mb-1">
                        Question {message.questionNumber}
                      </div>
                    )}
                    <p className="text-sm">{message.message}</p>
                  </div>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-start space-x-3 max-w-3xl">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100">
                    <Bot className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="bg-gray-100 text-gray-900 rounded-lg px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex space-x-4">
              <input
                ref={inputRef}
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your answer..."
                disabled={isLoading || session.status === "completed"}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
              />
              <Tooltip content="Send your message to the AI interviewer">
                <button
                  onClick={handleSendMessage}
                  disabled={
                    !currentMessage.trim() ||
                    isLoading ||
                    session.status === "completed"
                  }
                  className="px-6 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 relative"
                >
                  <Send className="w-4 h-4" />
                  <span>Send</span>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Camera Widget */}
      {proctoringStatus.isActive && proctoringStatus.cameraStream && (
        <FloatingCameraWidget
          stream={proctoringStatus.cameraStream}
          onClose={() => {
            // Optional: allow users to close camera view (but still keep monitoring)
            console.log('Camera view closed');
          }}
        />
      )}
    </div>
    </VerificationGuard>
  );
};

export default AIInterview;
